/**
 * Unified physics calculations for consistent cricket shot analysis
 * This ensures all distance calculations use the same realistic physics
 */

export interface ShotPhysicsParams {
  exitVelocity: number; // m/s
  launchAngle: number;  // degrees
  spinRate?: number;    // rpm (optional)
  quality?: 'poor' | 'fair' | 'good' | 'excellent'; // shot quality factor
}

/**
 * Calculate realistic cricket shot distance using unified physics
 * Accounts for air resistance, spin, and real-world conditions
 */
export function calculateRealisticDistance(params: ShotPhysicsParams): number {
  const { exitVelocity, launchAngle, spinRate = 2000, quality = 'good' } = params;
  
  // Basic projectile motion (vacuum distance)
  const g = 9.81;
  const angleRad = (launchAngle * Math.PI) / 180;
  const vacuumDistance = (Math.pow(exitVelocity, 2) * Math.sin(2 * angleRad)) / g;
  
  // Air resistance factor (cricket ball is not streamlined)
  const dragReduction = 0.55; // ~45% reduction from vacuum
  
  // Quality factor affects efficiency
  const qualityFactors = {
    poor: 0.75,     // Poor timing/contact
    fair: 0.85,     // Decent contact
    good: 0.95,     // Good contact
    excellent: 1.0  // Perfect contact
  };
  const qualityFactor = qualityFactors[quality];
  
  // Spin effect (slight reduction for non-optimal spin)
  const optimalSpin = 2000; // rpm
  const spinFactor = 1 - Math.abs(spinRate - optimalSpin) / 10000;
  const spinEffect = Math.max(0.9, spinFactor);
  
  // Real-world conditions (wind, temperature, humidity)
  const realWorldFactor = 0.92;
  
  // Combined realistic distance
  const realisticDistance = vacuumDistance * 
    dragReduction * 
    qualityFactor * 
    spinEffect * 
    realWorldFactor;
  
  // Cap at physical maximum for cricket
  const maxCricketDistance = 120; // meters (world-class maximum)
  
  return Math.min(maxCricketDistance, Math.max(0, realisticDistance));
}

/**
 * Calculate optimal distance for the same shot with perfect execution
 */
export function calculateOptimalDistance(params: ShotPhysicsParams): number {
  // For optimal calculation, use perfect quality and optimal conditions
  const optimalParams: ShotPhysicsParams = {
    ...params,
    quality: 'excellent',
    spinRate: 2000 // optimal spin rate
  };
  
  // Optimal exit velocity is limited to realistic maximum
  const maxExitVelocity = 55; // m/s (world-class maximum)
  if (optimalParams.exitVelocity > maxExitVelocity) {
    optimalParams.exitVelocity = maxExitVelocity;
  } else if (optimalParams.exitVelocity < 50) {
    // Room for improvement - add realistic improvement
    optimalParams.exitVelocity = Math.min(maxExitVelocity, params.exitVelocity * 1.1);
  }
  
  // Optimal angle (15-20 degrees typically best for distance)
  const currentAngle = params.launchAngle;
  if (currentAngle < 15) {
    optimalParams.launchAngle = 18; // Slightly higher for distance
  } else if (currentAngle > 25) {
    optimalParams.launchAngle = 20; // Bring down if too high
  }
  // Otherwise keep the same angle
  
  return calculateRealisticDistance(optimalParams);
}

/**
 * Ensure optimal is always >= actual (logical consistency)
 */
export function ensureLogicalDistances(actualDistance: number, optimalDistance: number): {
  actual: number;
  optimal: number;
  potentialGain: number;
} {
  // If optimal is less than actual, set optimal to actual + minimal improvement
  const correctedOptimal = Math.max(optimalDistance, actualDistance + 2);
  
  return {
    actual: actualDistance,
    optimal: Math.min(120, correctedOptimal), // Still cap at 120m
    potentialGain: correctedOptimal - actualDistance
  };
}